import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Alert, Container, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { isOidcEnabled, getOidcLoginUrl } from '../config/oidc';
import omniTekLogo from '../assets/omni-tek-full-logo-tp.png';

/**
 * LoginPage - Simplified version that auto-redirects to Authentik SSO
 * 
 * In OIDC mode: Automatically redirects to Authentik
 * In local/hybrid mode: Shows error (should use old LoginPage below)
 * 
 * The old LoginPage with manual SSO button and local login form is
 * commented out below - uncomment if you want to go back to that style.
 */
export const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isLoading, authMode, user } = useAuth();

  const callbackError = searchParams.get('error');
  const loggedOut = searchParams.get('logout') === 'true';

  // Redirect if already logged in (but not if just logged out)
  useEffect(() => {
    if (user && !isLoading && !loggedOut) {
      navigate('/', { replace: true });
    }
  }, [user, isLoading, navigate, loggedOut]);

  // Auto-redirect to OIDC in pure OIDC mode (unless there's an error to show or just logged out)
  useEffect(() => {
    if (!isLoading && !user && authMode === 'oidc' && isOidcEnabled() && !callbackError && !loggedOut) {
      window.location.href = getOidcLoginUrl();
    }
  }, [isLoading, user, authMode, callbackError, loggedOut]);

  // Show logged out confirmation
  if (loggedOut) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
        }}
      >
        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <Box
              component="img"
              src={omniTekLogo}
              alt="Omni-Tek"
              sx={{ height: 64, mb: 2 }}
            />
            <Typography variant="h5" gutterBottom>
              {t('auth.loggedOut', 'Du er nå logget ut')}
            </Typography>
            <Alert severity="success" sx={{ mb: 3 }}>
              {t('auth.loggedOutMessage', 'Du har blitt logget ut fra alle Omni-Tek-tjenester.')}
            </Alert>
            <Typography
              variant="body2"
              color="primary"
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => window.location.href = getOidcLoginUrl()}
            >
              {t('auth.loginAgain', 'Logg inn på nytt')}
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Show error from callback
  if (callbackError) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
        }}
      >
        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <Box
              component="img"
              src={omniTekLogo}
              alt="Omni-Tek"
              sx={{ height: 64, mb: 2 }}
            />
            <Typography variant="h5" gutterBottom>
              {t('auth.loginError')}
            </Typography>
            <Alert severity="error" sx={{ mb: 3 }}>
              {t('auth.authenticationFailed')}
            </Alert>
            <Typography
              variant="body2"
              color="primary"
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => window.location.href = getOidcLoginUrl()}
            >
              {t('auth.tryAgain')}
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Show loading while redirecting
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        {t('auth.redirectingToSSO')}
      </Typography>
    </Box>
  );
};

/* =============================================================================
 * OLD LOGIN PAGE - COMMENTED OUT
 * 
 * This is the original LoginPage with:
 * - Manual "Logg inn med SSO" button
 * - Local username/password form (for hybrid/local mode)
 * - Development credentials helper
 * 
 * Uncomment this and comment out the above if you want to go back to
 * showing a login page with a button instead of auto-redirect.
 * =============================================================================

import React, { useState } from 'react';
import {
  useNavigate,
  useLocation,
  Link,
  useSearchParams,
} from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Login as LoginIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { isOidcEnabled } from '../config/oidc';
import omniTekLogo from '../assets/omni-tek-full-logo-tp.png';

interface LocationState {
  from?: { pathname: string };
}

export const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, loginWithOidc, isLoading, authMode, user } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const from = (location.state as LocationState)?.from?.pathname || '/';
  const showLocalLogin = authMode === 'local' || authMode === 'hybrid';
  const showOidcLogin = isOidcEnabled();

  // Check for error from callback
  const callbackError = searchParams.get('error');

  // Redirect if already logged in
  React.useEffect(() => {
    if (user && !isLoading) {
      navigate(from, { replace: true });
    }
  }, [user, isLoading, navigate, from]);

  // Show callback error
  React.useEffect(() => {
    if (callbackError) {
      setError(t('auth.loginError'));
    }
  }, [callbackError, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await login({ email, password });
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error.message || t('auth.loginError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOidcLogin = () => {
    setError(null);
    loginWithOidc();
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.100',
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              component="img"
              src={omniTekLogo}
              alt="Omni-Tek"
              sx={{ height: 64, mb: 2 }}
            />
            <Typography variant="h4" component="h1" gutterBottom>
              {t('app.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('auth.loginSubtitle')}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {showOidcLogin && (
            <>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleOidcLogin}
                disabled={isSubmitting}
                startIcon={<LoginIcon />}
                sx={{
                  mb: 2,
                  bgcolor: 'primary.main',
                  textTransform: 'none',
                  py: 1.5,
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Logg inn med Omni-Tek SSO'
                )}
              </Button>

              {showLocalLogin && (
                <Divider sx={{ my: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('auth.orContinueWith')}
                  </Typography>
                </Divider>
              )}
            </>
          )}

          {showLocalLogin && (
            <>
              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label={t('auth.email')}
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  margin="normal"
                  required
                  autoComplete="email"
                  autoFocus={!showOidcLogin}
                />

                <TextField
                  fullWidth
                  label={t('auth.password')}
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  margin="normal"
                  required
                  autoComplete="current-password"
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                  sx={{ mt: 3, mb: 2 }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t('auth.login')
                  )}
                </Button>
              </form>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('auth.noAccount')}{' '}
                  <Link to="/register" style={{ color: 'inherit' }}>
                    {t('auth.register')}
                  </Link>
                </Typography>
              </Box>
            </>
          )}

          {import.meta.env.DEV && showLocalLogin && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {t('auth.devCredentials')}:
              </Typography>
              <Typography variant="caption" fontFamily="monospace">
                admin@example.com / password123
              </Typography>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

============================================================================= */
